/**
 * Pure helpers for the Studio design chat's contextual affordances — the
 * element chip above the composer, the ≤4 suggestion chips under it, and the
 * composer placeholder. All keyed off `StudioSelection` (studioTypes.ts).
 *
 * DOM-free by construction, so the contracts below are unit-tested in node
 * alongside the server suite — see chatSuggestions.test.ts. `StudioChatPanel`
 * (a later task) renders these; this module owns no JSX and no React state.
 */

import type { StudioSelection } from "./studioTypes";

/** A chip under the composer. "action" chips run a fixed flow (e.g. jump to
 *  the first flagged caption); "send" chips just submit their label as the
 *  chat message. */
export type Suggestion =
  | { label: string; kind: "send" }
  | { label: string; kind: "action"; action: "fix-caption" };

/** Cue text is truncated to this many VISIBLE chars before the ellipsis —
 *  keeps the chip short enough to sit on one line above the composer. */
const CHIP_TEXT_MAX = 28;

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/** The chip naming the selected element, shown above the composer. Null
 *  means nothing is selected (composer talks about the clip generally). */
export function selectionChipLabel(sel: StudioSelection, cueText: string | null): string | null {
  switch (sel.el) {
    case "caption":
      if (sel.cueIdx != null) {
        const text = cueText != null ? truncate(cueText, CHIP_TEXT_MAX) : "";
        return `Caption ${sel.cueIdx + 1} · “${text}”`;
      }
      return "Captions";
    case "logo":
      return "Logo";
    case "speaker":
      return "Speaker card";
    case "video":
      return "Footage";
    default:
      return null;
  }
}

/** Placeholder text for the chat composer, tailored to what's selected. */
export function composerPlaceholder(sel: StudioSelection): string {
  switch (sel.el) {
    case "caption":
      return sel.cueIdx != null
        ? "What should change about this caption?"
        : "What should change about the captions?";
    case "logo":
      return "What should change about the logo?";
    case "speaker":
      return "What should change about the speaker card?";
    case "video":
      return "What should change about the footage?";
    default:
      return 'e.g. "the captions are too big"';
  }
}

const send = (label: string): Suggestion => ({ label, kind: "send" });

/** ≤4 contextual suggestion chips for the current selection (spec §4). */
export function suggestionsFor(sel: StudioSelection, opts: { hasCues: boolean }): Suggestion[] {
  switch (sel.el) {
    case "caption": {
      if (sel.cueIdx != null) {
        // One caption has no size of its own — "smaller" would restyle the
        // whole clip (2026-09-17, the fontSizePx incident). Split / reword /
        // re-break are the honest per-caption moves; the agent's per-caption
        // scope is locked to exactly those.
        const suggestions = [send("Split it in two"), send("Say it shorter"), send("Break the line differently")];
        if (sel.word) suggestions.push(send(`Make “${sel.word}” gold`));
        return suggestions;
      }
      return [send("Move captions higher"), send("A bit smaller"), send("Warmer highlight")];
    }
    case "logo":
      return [send("Smaller"), send("Move it to the corner"), send("Lower")];
    case "speaker":
      return [send("Bigger name"), send("Show it longer")];
    case "video":
      return [send("Brighter"), send("Darker")];
    default: {
      const suggestions: Suggestion[] = [];
      if (opts.hasCues) {
        suggestions.push({ label: "Fix a caption", kind: "action", action: "fix-caption" });
      }
      suggestions.push(send("Move captions higher"), send("Warmer highlight"), send("Logo smaller"));
      return suggestions;
    }
  }
}
